import React, { useState, useEffect } from 'react';
import { X, UserPlus, Users, Mail, Trash2, Clock, Check, Crown } from 'lucide-react';
import { sharingAPI } from '../firebase';
import './ShareProjectModal.css';

const ShareProjectModal = ({ project, userId, userName, onClose }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [owner, setOwner] = useState(null);

  // Determine if current user is the owner
  const isOwner = !project._isShared || project._ownerId === userId;
  const projectOwnerId = project._ownerId || userId;

  useEffect(() => {
    loadCollaborators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const loadCollaborators = async () => {
    const result = await sharingAPI.getProjectCollaborators(project.id);
    if (result.success) {
      setCollaborators(result.collaborators);
      setPendingInvites(result.pendingInvites);
      setOwner(result.owner);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setMessage(null);

    const result = await sharingAPI.shareProject(
      projectOwnerId,
      project.id,
      project.name,
      email.trim(),
      role
    );

    setLoading(false);

    if (result.success) {
      setMessage({ type: 'success', text: result.message });
      setEmail('');
      loadCollaborators();
    } else {
      setMessage({ type: 'error', text: result.error });
    }
  };

  const handleRemoveCollaborator = async (targetUserId) => {
    const result = await sharingAPI.removeCollaborator(projectOwnerId, project.id, targetUserId);
    if (result.success) {
      loadCollaborators();
    }
  };

  const handleCancelInvite = async (inviteCode) => {
    const result = await sharingAPI.cancelInvite(project.id, inviteCode);
    if (result.success) {
      loadCollaborators();
    }
  };

  const handleLeaveProject = async () => {
    const result = await sharingAPI.leaveProject(userId, project.id, projectOwnerId);
    if (result.success) {
      onClose();
    }
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <div className="share-modal-title">
            <Users size={20} />
            <h3>Share "{project.name}"</h3>
          </div>
          <button className="share-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="share-modal-content">
          {/* Share form - only for owner */}
          {isOwner && (
            <form className="share-form" onSubmit={handleShare}>
              <div className="share-input-row">
                <div className="share-input-wrapper">
                  <Mail size={16} />
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  disabled={loading}
                >
                  <option value="editor">Can edit</option>
                  <option value="viewer">Can view</option>
                </select>
                <button type="submit" disabled={loading || !email.trim()}>
                  <UserPlus size={16} />
                  {loading ? 'Sharing...' : 'Share'}
                </button>
              </div>
            </form>
          )}

          {message && (
            <div className={`share-message ${message.type}`}>
              {message.type === 'success' ? <Check size={16} /> : null}
              {message.text}
            </div>
          )}

          {/* Collaborators list */}
          <div className="collaborators-section">
            <h4>People with access</h4>

            {/* Owner */}
            {owner && (
              <div className="collaborator-item owner">
                <div className="collaborator-avatar">
                  <Crown size={14} />
                </div>
                <div className="collaborator-info">
                  <span className="collaborator-name">
                    {owner.name || owner.email || 'Owner'}
                    {owner.id === userId && ' (you)'}
                  </span>
                  <span className="collaborator-email">{owner.email}</span>
                </div>
                <span className="collaborator-role owner-badge">Owner</span>
              </div>
            )}

            {/* Active collaborators */}
            {collaborators.map((collab) => (
              <div key={collab.id} className="collaborator-item">
                <div className="collaborator-avatar">
                  {collab.email?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="collaborator-info">
                  <span className="collaborator-name">
                    {collab.email}
                    {collab.id === userId && ' (you)'}
                  </span>
                  <span className="collaborator-role-text">{collab.role}</span>
                </div>
                {isOwner && collab.id !== userId && (
                  <button
                    className="remove-btn"
                    onClick={() => handleRemoveCollaborator(collab.id)}
                    title="Remove access"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}

            {/* Pending invites */}
            {pendingInvites.length > 0 && isOwner && (
              <>
                <h4 className="pending-header">
                  <Clock size={14} />
                  Pending invites
                </h4>
                {pendingInvites.map((invite) => (
                  <div key={invite.code} className="collaborator-item pending">
                    <div className="collaborator-avatar pending">
                      <Mail size={14} />
                    </div>
                    <div className="collaborator-info">
                      <span className="collaborator-name">{invite.email}</span>
                      <span className="collaborator-role-text">
                        {invite.role} - Pending
                      </span>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => handleCancelInvite(invite.code)}
                      title="Cancel invite"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </>
            )}

            {collaborators.length === 0 && pendingInvites.length === 0 && (
              <p className="no-collaborators">
                No collaborators yet. Share this project to collaborate in real-time.
              </p>
            )}
          </div>

          {/* Leave project button for collaborators */}
          {!isOwner && (
            <div className="leave-section">
              <button className="leave-btn" onClick={handleLeaveProject}>
                Leave project
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareProjectModal;
